import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { CheckSquare, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReadinessCheck = () => {
  const [checks, setChecks] = useState({
    mietvertrag_vollstaendig: false,
    indexierung_klar: false,
    grundbuch_verfuegbar: false,
    plaene_vorhanden: false,
    mietzahlungen_aktuell: false,
    verkaufsziel_klar: false
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const questions = [
    { key: 'mietvertrag_vollstaendig', label: 'Mietvertrag und alle Nachträge liegen vollständig vor' },
    { key: 'indexierung_klar', label: 'Indexierung oder Staffelmieten-Regelung ist klar dokumentiert' },
    { key: 'grundbuch_verfuegbar', label: 'Grundbuchauszug und Informationen zu Lasten sind verfügbar' },
    { key: 'plaene_vorhanden', label: 'Pläne und Flächenaufstellung sind vorhanden' },
    { key: 'mietzahlungen_aktuell', label: 'Mietzahlungen sind aktuell ohne Rückstände' },
    { key: 'verkaufsziel_klar', label: 'Verkaufsziel (Zeitrahmen, Preisvorstellung) ist definiert' }
  ];

  const handleCheck = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tool/readiness`, checks);
      setResult(response.data);
      toast.success('Check abgeschlossen!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Check');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'verkaufsbereit') return 'var(--success-600)';
    if (status === 'vorbereiten') return 'var(--warning-600)';
    return 'var(--error-600)';
  };

  const getStatusBg = (status) => {
    if (status === 'verkaufsbereit') return 'var(--success-100)';
    if (status === 'vorbereiten') return 'var(--warning-100)';
    return 'var(--error-100)';
  };

  const getStatusLabel = (status) => {
    if (status === 'verkaufsbereit') return 'Verkaufsbereit';
    if (status === 'vorbereiten') return 'Vorbereitung notwendig';
    return 'Kritisch - noch nicht verkaufsbereit';
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Verkaufs-Readiness Check – Ist Ihre Immobilie verkaufsbereit?"
        description="Prüfen Sie in 2 Minuten ob Ihre Handelsimmobilie verkaufsbereit ist. Identifizieren Sie fehlende Unterlagen und notwendige Vorbereitungsschritte."
        url="/tools/readiness"
      />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6 mx-auto"></div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
              Verkaufs-Readiness Check
            </h1>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--gray-600)' }}>
              Prüfen Sie in 2 Minuten, ob Ihre Handelsimmobilie verkaufsbereit ist
            </p>
          </div>

          <Card className="glass-card p-8 mb-8">
            <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>
              Checkliste
            </h2>
            <p className="mb-6" style={{ color: 'var(--gray-700)' }}>
              Bitte kreuzen Sie an, was bereits vorhanden oder geklärt ist:
            </p>

            <div className="space-y-4">
              {questions.map((q) => (
                <div key={q.key} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <Checkbox
                    id={q.key}
                    checked={checks[q.key]}
                    onCheckedChange={(checked) => setChecks({ ...checks, [q.key]: checked })}
                    className="mt-1"
                  />
                  <Label
                    htmlFor={q.key}
                    className="text-base cursor-pointer flex-1"
                    style={{ color: 'var(--gray-800)' }}
                  >
                    {q.label}
                  </Label>
                </div>
              ))}
            </div>

            <Button
              onClick={handleCheck}
              disabled={loading}
              className="w-full mt-8 btn-gold-gradient text-white border-0 font-semibold py-6 text-lg"
            >
              <CheckSquare className="mr-2 h-5 w-5" />
              {loading ? 'Prüfe...' : 'Verkaufsbereitschaft prüfen'}
            </Button>
          </Card>

          {result && (
            <div className="space-y-6">
              <Card
                className="glass-card p-8"
                style={{ borderLeft: `6px solid ${getStatusColor(result.status)}` }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--navy-900)' }}>
                      Ergebnis
                    </h2>
                    <div
                      className="inline-block px-4 py-2 rounded-full text-lg font-bold"
                      style={{
                        backgroundColor: getStatusBg(result.status),
                        color: getStatusColor(result.status)
                      }}
                    >
                      {getStatusLabel(result.status)}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm mb-1" style={{ color: 'var(--gray-600)' }}>Erfüllt:</p>
                    <p className="text-4xl font-bold" style={{ color: 'var(--gold-600)' }}>
                      {result.score}/6
                    </p>
                  </div>
                </div>

                <div className="p-6 rounded-lg" style={{ backgroundColor: getStatusBg(result.status) }}>
                  <h3 className="font-bold mb-3" style={{ color: getStatusColor(result.status) }}>
                    Empfehlung:
                  </h3>
                  <p style={{ color: 'var(--gray-800)' }}>
                    {result.empfehlung}
                  </p>
                </div>
              </Card>

              {result.todos.length > 0 && (
                <Card className="glass-card p-8">
                  <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>
                    Ihre To-do Liste
                  </h3>
                  <div className="space-y-3">
                    {result.todos.map((todo, index) => (
                      <div key={index} className="flex items-start gap-3 p-4 bg-red-50 rounded-lg">
                        <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--error-600)' }} />
                        <p className="text-sm" style={{ color: 'var(--gray-800)' }}>{todo}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              <Card className="glass-card p-8">
                <h3 className="text-xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>
                  Nächste Schritte
                </h3>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/ersteinschaetzung" className="flex-1">
                    <Button className="w-full btn-gold-gradient text-white border-0 font-semibold py-6">
                      Vorbereitungsgespräch buchen
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link to="/kontakt" className="flex-1">
                    <Button
                      variant="outline"
                      className="w-full font-semibold py-6"
                      style={{ borderColor: 'var(--navy-600)', color: 'var(--navy-600)' }}
                    >
                      Unterlagen-Checkliste anfragen
                    </Button>
                  </Link>
                </div>
              </Card>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ReadinessCheck;
